/**
 * Wave BC — PFD Document-First Workflow.
 *
 * Implements `Capexara Process Industry_PFD_Document_First_Workflow.md` end-to-end.
 *
 * UX flow (verbatim from spec):
 *   1. User clicks [Run Stage] → AI creates a PFD Document Index (1 call).
 *   2. Document list appears — one row per sheet with [Generate] button.
 *   3. User clicks [Generate] (one-at-a-time) or [Generate All] (background queue).
 *   4. Each generated PFD opens as a new tab in the [PFD Viewer].
 *   5. Background polling — generation continues even if user navigates away.
 *   6. OPC arrows on the canvas are clickable → jump to connected PFD tab.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../../lib/api";
import PfdEditor from "./PfdEditor";
import ApsDrawingViewer from "./ApsDrawingViewer";
import { Loader2, FileText, Layers, Eye, Play, RefreshCcw, AlertTriangle,
   ChevronDown, ChevronRight, Edit3, Lock, Unlock, Download, History,
   GitMerge, Split, Table, AlertCircle, CheckCircle2,
   PauseCircle, PlayCircle, XCircle, Trash2, Move, FileSpreadsheet,
   ZoomIn, ZoomOut, Maximize2, Minimize2, Expand, Hand, Pencil } from "lucide-react";

// FNV-1a 64-bit -> 16-hex client-side hash for drift detection.
function _hashSvgFnv(s) {
  if (!s) return "";
  let h1 = 0x811c9dc5, h2 = 0xdeadbeef;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 ^ c, 2166136261) >>> 0;
  }
  return (h1.toString(16).padStart(8, "0")
        + h2.toString(16).padStart(8, "0")).slice(0, 16);
}

const COL = {
  bg: "var(--pf-bg, #020617)",
  surface: "var(--pf-surface, #0f1729)",
  surface2: "var(--pf-surface-2, #1a2332)",
  border: "var(--pf-border, #1e293b)",
  text: "var(--pf-text, #e2e8f0)",
  dim: "var(--pf-text-muted, #94a3b8)",
  spark: "var(--pf-spark, #22d3ee)",
  amber: "#f59e0b",
  green: "#22c55e",
  red: "#ef4444",
};
const mono = { fontFamily: "'JetBrains Mono', 'Fira Code', monospace" };

const STATUS_ICONS = {
  not_started: "⬜",
  queued:      "⏸",
  generating:  "🔄",
  complete:    "✅",
  failed:      "❌",
};

function formatStreamCount(s) {
  if (typeof s === "number") return `${s} streams`;
  return "";
}

export default function PfdDocumentFirstPanel({ projectId }) {
  const [activeTab, setActiveTab] = useState("documents"); // documents | viewer | masters | quality
  const [index, setIndex] = useState(null);
  const [state, setState] = useState(null);
  const [drawings, setDrawings] = useState({});
  const [activeSheetId, setActiveSheetId] = useState(null);
  const [openSheetIds, setOpenSheetIds] = useState([]);
  const [creatingIndex, setCreatingIndex] = useState(false);
  const [err, setErr] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  // Wave BC.1 — edit / approve / quality / master / revisions state
  const [editingSheet, setEditingSheet] = useState(null); // {sheet_number, title, description}
  const [splittingSheet, setSplittingSheet] = useState(null); // sheet object
  const [mergingSheets, setMergingSheets] = useState([]); // array of sheet_numbers
  const [qualityChecks, setQualityChecks] = useState([]);
  const [masterStreams, setMasterStreams] = useState([]);
  const [masterEquipment, setMasterEquipment] = useState([]);
  const [revisions, setRevisions] = useState({}); // sheet_id → list
  const [showRecovery, setShowRecovery] = useState(false);
  // Wave BD — equipment reassignment + highlighted item
  const [reassigning, setReassigning] = useState(null); // { from_sheet, equipment_tags: [] }
  const [highlighted, setHighlighted] = useState({ stream: null, equipment: null });
  // Wave PR.1 — viewer fullscreen toggle (covers entire window)
  const [viewerFullscreen, setViewerFullscreen] = useState(false);
  // Esc to exit fullscreen
  useEffect(() => {
    if (!viewerFullscreen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setViewerFullscreen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerFullscreen]);
  const [opcPulse, setOpcPulse] = useState(null); // { sheet_id, stream_id }
  // CH.3 — viewer zoom / pan
  const [viewerZoom, setViewerZoom] = useState(1);
  const [viewerPan, setViewerPan] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });

  // CH.3 — Keyboard shortcuts when viewer is active.
  useEffect(() => {
    if (activeTab !== "viewer") return;
    const onKey = (e) => {
      // Ignore when typing in inputs/textareas
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA"
          || e.target?.isContentEditable) return;
      if (e.key === "0") { setViewerZoom(1); setViewerPan({ x: 0, y: 0 }); }
      else if (e.key === "+" || e.key === "=") setViewerZoom((z) => Math.min(4, +(z + 0.15).toFixed(2)));
      else if (e.key === "-" || e.key === "_") setViewerZoom((z) => Math.max(0.2, +(z - 0.15).toFixed(2)));
      else if (e.key === "f" || e.key === "F") setViewerFullscreen((v) => !v);
      else if (e.key === "Escape" && viewerFullscreen) setViewerFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeTab, viewerFullscreen]);
  const pollTimer = useRef(null);

  // ── Initial load ──
  const loadIndex = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/pfd/index`);
      if (r.data && Array.isArray(r.data.sheets) && r.data.sheets.length) {
        setIndex(r.data);
      } else {
        setIndex(null);
      }
    } catch (e) { /* 404/500 → no index yet */ }
  }, [projectId]);

  const loadState = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/pfd-generation-state`);
      setState(r.data);
    } catch (e) { /* no state yet */ }
  }, [projectId]);

  const loadDrawings = useCallback(async () => {
    try {
      // The /drawings endpoint is currently shadowed by the legacy
      // drawing-register router (returns a flat list from db.drawings).
      // Hit the PFD-specific endpoint instead so we always get the
      // PFD/BFD sheets + their canvas_svg payloads. Fall back to the
      // shadowed endpoint for older deployments.
      let list = [];
      try {
        const r1 = await api.get(`/projects/${projectId}/pfd/drawings`);
        list = r1.data?.drawings || [];
      } catch {
        const r = await api.get(`/projects/${projectId}/drawings`);
        const raw = r.data;
        const all = Array.isArray(raw) ? raw : (raw?.drawings || []);
        // PFD rows are identified by ``sheet_id`` (drawing-register rows
        // use ``drawing_id`` instead).
        list = all.filter((d) => d && d.sheet_id);
      }
      // Preserve already-fetched ``canvas_svg`` payloads across reloads —
      // the LIST endpoint omits ``canvas_svg`` to keep the response small.
      // If we replaced the whole map we'd lose every previously-loaded
      // drawing's SVG, which is exactly the bug where clicking a tab on
      // a previously-generated PFD showed blank after a new one was made.
      setDrawings((prev) => {
        const next = {};
        for (const d of list) {
          const had = prev[d.sheet_id];
          next[d.sheet_id] = (had && had.canvas_svg && !d.canvas_svg)
            ? { ...d, canvas_svg: had.canvas_svg }
            : d;
        }
        return next;
      });
    } catch (e) { /* */ }
  }, [projectId]);

  useEffect(() => {
    loadIndex();
    loadState();
    loadDrawings();
  }, [loadIndex, loadState, loadDrawings]);

  // Wave BC.1 — interrupt-recovery: if state.current_job exists but no
  // active polling AND not all_complete, prompt user to resume.
  useEffect(() => {
    if (state && state.current_job && !state.all_complete && !pollTimer.current) {
      setShowRecovery(true);
    }
  }, [state]);

  // Wave BC.1 — quality + master loaders
  const loadQuality = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/pfd/quality-checks`);
      setQualityChecks(r.data?.checks || []);
    } catch { /* */ }
  }, [projectId]);
  const loadMasters = useCallback(async () => {
    try {
      const [s, e] = await Promise.all([
        api.get(`/projects/${projectId}/pfd/master-stream-table`),
        api.get(`/projects/${projectId}/pfd/master-equipment-list`),
      ]);
      setMasterStreams(s.data?.rows || []);
      setMasterEquipment(e.data?.rows || []);
    } catch { /* */ }
  }, [projectId]);
  useEffect(() => {
    if (index) { loadQuality(); loadMasters(); }
  }, [index, loadQuality, loadMasters]);

  // Wave BC.1 — split/merge/rename/approve/unlock
  const splitSheet = async (sheet_number, groups) => {
    try {
      const r = await api.post(`/projects/${projectId}/pfd/index/split-sheet`,
                                  { sheet_number, groups });
      setIndex(r.data?.index);
      setSplittingSheet(null);
      await loadQuality();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const mergeSheetsCall = async (sheet_numbers) => {
    try {
      const r = await api.post(`/projects/${projectId}/pfd/index/merge-sheets`,
                                  { sheet_numbers });
      setIndex(r.data?.index);
      setMergingSheets([]);
      await loadQuality();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const renameSheet = async (sheet_number, payload) => {
    try {
      const r = await api.put(`/projects/${projectId}/pfd/index/sheet/${sheet_number}`, payload);
      setIndex(r.data?.index);
      setEditingSheet(null);
      await loadQuality();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const approveSheet = async (sheet_number) => {
    try {
      await api.post(`/projects/${projectId}/pfd/sheet/${sheet_number}/approve`);
      await loadDrawings();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const unlockSheet = async (sheet_number) => {
    try {
      await api.post(`/projects/${projectId}/pfd/sheet/${sheet_number}/unlock`);
      await loadDrawings();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  const loadRevisions = async (sheet_number, sheet_id) => {
    try {
      const r = await api.get(`/projects/${projectId}/pfd/sheet/${sheet_number}/revisions`);
      setRevisions((p) => ({ ...p, [sheet_id]: r.data?.revisions || [] }));
    } catch { /* */ }
  };
  const exportSvg = (sheet_number) => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/${sheet_number}.svg`;
    window.open(url, "_blank");
  };
  const exportPdf = (sheet_number) => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/${sheet_number}.pdf`;
    window.open(url, "_blank");
  };
  const exportZip = () => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/all.zip`;
    window.open(url, "_blank");
  };
  // Feb 2026 — Line List Export (every pipe with ISO line no., NPS,
  // service, material, T/P/flow). Streams data from /line-list/export.xlsx.
  const exportLineList = () => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/line-list/export.xlsx`;
    window.open(url, "_blank");
  };
  // Wave BD — additional exports
  const exportDxf = (sheet_number) => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/${sheet_number}.dxf`;
    window.open(url, "_blank");
  };
  const exportXlsx = (sheet_number) => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/${sheet_number}.xlsx`;
    window.open(url, "_blank");
  };
  const exportIndexXlsx = () => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/index.xlsx`;
    window.open(url, "_blank");
  };
  const exportMasterStreamsXlsx = () => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/master-streams.xlsx`;
    window.open(url, "_blank");
  };
  const exportMasterEquipmentXlsx = () => {
    const url = `${api.defaults.baseURL || ""}/projects/${projectId}/pfd/export/master-equipment.xlsx`;
    window.open(url, "_blank");
  };
  // Wave BD — Pause / Resume / Cancel queue
  const pauseQueue = async () => {
    try { await api.post(`/projects/${projectId}/pfd/pause`); }
    catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
    finally { await loadState(); }
  };
  const resumeQueue = async () => {
    try { await api.post(`/projects/${projectId}/pfd/resume`); }
    catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
    finally { await loadState(); }
  };
  const cancelQueue = async () => {
    if (!window.confirm("Cancel PFD generation queue? Already-completed sheets are kept.")) return;
    try { await api.post(`/projects/${projectId}/pfd/cancel`); }
    catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
    finally { await loadState(); }
  };
  // Wave BD — Reassign equipment between sheets
  const reassignEquipment = async (from_sheet, to_sheet, equipment_tags) => {
    try {
      const r = await api.post(`/projects/${projectId}/pfd/index/reassign`,
                                  { from_sheet, to_sheet, equipment_tags });
      setIndex(r.data?.index);
      setReassigning(null);
      await loadQuality();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };
  // Wave BD — Remove sheet
  const removeSheet = async (sheet_number) => {
    if (!window.confirm(`Remove PFD-${String(sheet_number).padStart(3, "0")}? Its equipment will be moved to the next remaining sheet.`)) return;
    try {
      const r = await api.delete(`/projects/${projectId}/pfd/index/sheet/${sheet_number}`);
      setIndex(r.data?.index);
      await loadDrawings();
      await loadQuality();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || e.message); }
  };

  // ── Background polling while generating ──
  useEffect(() => {
    const isRunning = state && !state.all_complete && (state.current_job || state.paused || (state.rows || []).some(r => r.status === "generating" || r.status === "queued"));
    if (!isRunning) {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    if (pollTimer.current) return;
    pollTimer.current = setInterval(async () => {
      await loadState();
      await loadDrawings();
    }, 2500);
    return () => {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
    };
  }, [state, loadState, loadDrawings]);

  // ── Run Stage = create the PFD index ──
  const runStage = async () => {
    setCreatingIndex(true); setErr("");
    try {
      const r = await api.post(`/projects/${projectId}/pfd/create-index`, {});
      setIndex(r.data?.index);
      await loadState();
      await loadDrawings();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message || "Failed to create PFD index");
    } finally { setCreatingIndex(false); }
  };

  // ── Generate ONE sheet ──
  const generateOne = async (sheet_number, sheet_id, title) => {
    // Optimistically mark generating
    setState((prev) => {
      const rows = (prev?.rows || []).map((r) =>
        r.sheet_id === sheet_id ? { ...r, status: "generating" } : r,
      );
      return { ...(prev || {}), rows };
    });
    try {
      await api.post(`/projects/${projectId}/pfd/generate/${sheet_number}`);
      await loadState();
      await loadDrawings();
      // Open the freshly-generated sheet as a tab + activate
      openInViewer(sheet_id);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
      setState((prev) => {
        const rows = (prev?.rows || []).map((r) =>
          r.sheet_id === sheet_id ? { ...r, status: "failed", error: "Render failed" } : r,
        );
        return { ...(prev || {}), rows };
      });
    }
  };

  // ── Generate ALL (background) ──
  const generateAll = async () => {
    try {
      await api.post(`/projects/${projectId}/pfd/generate-all`, { only_missing: true });
      await loadState();
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  // ── Open a generated sheet in the viewer ──
  // ``loadDrawings`` populates ``drawings[sheet_id]`` from the LIST endpoint
  // which excludes ``canvas_svg`` to keep the payload small. So even if a
  // sheet was generated in a previous session, ``drawings[sheet_id]`` will
  // be defined but ``.canvas_svg`` will be undefined — clicking that tab
  // would render the empty-state placeholder. Always re-fetch when the SVG
  // is missing so previously-created PFDs render on tab click.
  const openInViewer = useCallback(async (sheet_id) => {
    if (!drawings[sheet_id] || !drawings[sheet_id].canvas_svg) {
      try {
        const r = await api.get(`/projects/${projectId}/drawings/${sheet_id}`);
        setDrawings((p) => ({ ...p, [sheet_id]: r.data }));
      } catch { return; }
    }
    setOpenSheetIds((p) => p.includes(sheet_id) ? p : [...p, sheet_id]);
    setActiveSheetId(sheet_id);
    setActiveTab("viewer");
  }, [projectId, drawings]);

  // ── OPC click → jump to connected sheet's tab ──
  const onCanvasClick = useCallback((e) => {
    // OPC navigation
    const opc = e.target.closest('[data-opc-target-sheet]');
    if (opc) {
      const sheetNumStr = opc.getAttribute("data-opc-target-sheet");
      const streamId = opc.getAttribute("data-opc-stream");
      if (sheetNumStr && sheetNumStr !== "null") {
        const n = Number(sheetNumStr);
        const targetSheetId = n === 0 ? "BFD-001" : `PFD-${String(n).padStart(3, "0")}`;
        if (drawings[targetSheetId]) {
          openInViewer(targetSheetId);
          // Pulse-highlight the matching OPC on the destination sheet
          setOpcPulse({ sheet_id: targetSheetId, stream_id: streamId });
          window.setTimeout(() => setOpcPulse(null), 2200);
        } else {
          if (window.confirm(`Sheet ${targetSheetId} has not been generated yet. Generate it now?`)) {
            const idxSheet = (index?.sheets || []).find((s) => (s.sheet_number === 0 ? "BFD-001" : `PFD-${String(s.sheet_number).padStart(3, "0")}`) === targetSheetId);
            if (idxSheet) generateOne(idxSheet.sheet_number, targetSheetId, idxSheet.title);
          }
        }
      }
      return;
    }
    // Stream click → highlight in master streams table
    const stream = e.target.closest('[data-stream-id]');
    if (stream) {
      const sid = stream.getAttribute("data-stream-id");
      if (sid) {
        setHighlighted((p) => ({
          ...p, stream: p.stream === sid ? null : sid,
        }));
        setActiveTab("masters");
        window.setTimeout(() => {
          const row = document.querySelector(`[data-testid="master-stream-row-${sid}"]`);
          row?.scrollIntoView({ block: "center", behavior: "smooth" });
        }, 50);
      }
      return;
    }
    // Equipment click → highlight in master equipment table
    const eq = e.target.closest('[data-equipment-tag]');
    if (eq) {
      const tag = eq.getAttribute("data-equipment-tag");
      if (tag) {
        setHighlighted((p) => ({
          ...p, equipment: p.equipment === tag ? null : tag,
        }));
        setActiveTab("masters");
        window.setTimeout(() => {
          const row = document.querySelector(`[data-testid="master-equipment-row-${tag}"]`);
          row?.scrollIntoView({ block: "center", behavior: "smooth" });
        }, 50);
      }
    }
  }, [drawings, index, openInViewer]);

  // Paint highlights/pulses inside the active SVG by toggling CSS classes on
  // matching `[data-stream-id]` and `[data-equipment-tag]` nodes. This keeps
  // highlight logic out of the SVG generator (deterministic renderer) and
  // lives entirely in the React layer.
  useEffect(() => {
    const root = document.querySelector('[data-testid="pfd-viewer-canvas"]');
    if (!root) return;
    // Reset
    root.querySelectorAll(".pfd-hi").forEach((n) => n.classList.remove("pfd-hi"));
    root.querySelectorAll(".pfd-opc-pulse").forEach((n) => n.classList.remove("pfd-opc-pulse"));
    if (highlighted.stream) {
      root.querySelectorAll(`[data-stream-id="${CSS.escape(highlighted.stream)}"]`)
        .forEach((n) => n.classList.add("pfd-hi"));
    }
    if (highlighted.equipment) {
      root.querySelectorAll(`[data-equipment-tag="${CSS.escape(highlighted.equipment)}"]`)
        .forEach((n) => n.classList.add("pfd-hi"));
    }
    if (opcPulse && opcPulse.sheet_id === activeSheetId) {
      root.querySelectorAll(`[data-opc-stream="${CSS.escape(opcPulse.stream_id || "")}"]`)
        .forEach((n) => n.classList.add("pfd-opc-pulse"));
    }
  }, [highlighted, opcPulse, activeSheetId, drawings]);

  // ── Render decisions ──
  const sheets = (index?.sheets || []).slice().sort((a, b) => a.sheet_number - b.sheet_number);
  const rowMap = useMemo(() => {
    const m = {};
    for (const r of (state?.rows || [])) m[r.sheet_id] = r;
    return m;
  }, [state]);

  const generatedCount = sheets.filter((s) => {
    const sid = s.sheet_number === 0 ? "BFD-001" : `PFD-${String(s.sheet_number).padStart(3, "0")}`;
    return rowMap[sid]?.status === "complete" || drawings[sid];
  }).length;

  const isAnyRunning = (state?.rows || []).some((r) => r.status === "generating" || r.status === "queued");
  const progress = state?.progress;

  // ─────────────── RENDER ───────────────
  return (
    <div data-testid="pfd-document-first-panel"
         style={{ display: "flex", flexDirection: "column", height: "100%",
                    background: COL.bg, color: COL.text, ...mono, fontSize: 12 }}>
      {/* Header / tab bar */}
      <div style={{ display: "flex", alignItems: "center", padding: "10px 18px",
                      borderBottom: `1px solid ${COL.border}`, gap: 16,
                      background: COL.surface }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: COL.spark }}>
          B.2 — PROCESS FLOW DIAGRAMS
        </div>
        <div style={{ flex: 1 }}/>
        <div style={{ display: "flex", gap: 4 }}>
          <button data-testid="pfd-tab-documents"
                  onClick={() => setActiveTab("documents")}
                  style={tabStyle(activeTab === "documents")}>
            <FileText size={12}/> Documents
            {index ? (
              <span style={{ marginLeft: 6, color: COL.dim }}>
                {generatedCount} / {sheets.length}
              </span>
            ) : null}
          </button>
          <button data-testid="pfd-tab-viewer"
                  onClick={() => setActiveTab("viewer")}
                  style={tabStyle(activeTab === "viewer")}>
            <Eye size={12}/> PFD Viewer
            {openSheetIds.length > 0 && (
              <span style={{ marginLeft: 6, color: COL.dim }}>({openSheetIds.length})</span>
            )}
          </button>
          <button data-testid="pfd-tab-editor"
                  onClick={() => setActiveTab("editor")}
                  style={tabStyle(activeTab === "editor")}
                  title="Interactive PFD editor — drag equipment, connect nozzles, edit tags/labels">
            <Edit3 size={12}/> Editor
          </button>
          <button data-testid="pfd-tab-quality"
                  onClick={() => { setActiveTab("quality"); loadQuality(); }}
                  style={tabStyle(activeTab === "quality")}>
            <AlertCircle size={12}/> Quality
            {qualityChecks.length > 0 && (
              <span style={{ marginLeft: 6, color: qualityChecks.some(c => c.severity === "error") ? "#fca5a5" : COL.amber, fontWeight: 700 }}>
                {qualityChecks.length}
              </span>
            )}
          </button>
          <button data-testid="pfd-tab-masters"
                  onClick={() => { setActiveTab("masters"); loadMasters(); }}
                  style={tabStyle(activeTab === "masters")}>
            <Table size={12}/> Master Tables
          </button>
        </div>
      </div>

      {/* Background progress strip (visible on both tabs while generating) */}
      {(isAnyRunning || state?.paused) && progress && (
        <div data-testid="pfd-progress-strip"
             style={{ padding: "8px 18px", background: "rgba(34,211,238,0.06)",
                        borderBottom: `1px solid ${COL.border}`,
                        display: "flex", alignItems: "center", gap: 12 }}>
          {state?.paused
            ? <PauseCircle size={13} style={{ color: COL.amber }}/>
            : <Loader2 className="pf-spin" size={13} style={{ color: COL.spark }}/>}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: COL.text, marginBottom: 4 }}>
              {progress.message || "Generating PFDs…"}
            </div>
            <div style={{ width: "100%", height: 5,
                            background: "rgba(255,255,255,0.06)", borderRadius: 3 }}>
              <div data-testid="pfd-progress-bar"
                   style={{ width: `${progress.percent || 0}%`, height: "100%",
                              background: `linear-gradient(90deg, ${COL.spark}, #6ad)`,
                              borderRadius: 3, transition: "width 350ms ease" }}/>
            </div>
          </div>
          <div style={{ fontSize: 11, color: COL.dim, fontWeight: 700 }}>
            {progress.current || 0} / {progress.total || 0}
            {" · "}
            <span data-testid="pfd-progress-percent">{progress.percent || 0}%</span>
            {progress.eta_seconds != null && progress.eta_seconds > 0 && (
              <span data-testid="pfd-progress-eta" style={{ marginLeft: 8 }}>
                · ETA {progress.eta_seconds < 60
                  ? `${progress.eta_seconds}s`
                  : `${Math.floor(progress.eta_seconds / 60)}m ${Math.round(progress.eta_seconds % 60)}s`}
              </span>
            )}
          </div>
          {state?.paused ? (
            <button data-testid="pfd-resume-queue" onClick={resumeQueue}
                    title="Resume generation"
                    style={{ ...iconBtn(false, true), color: COL.green }}>
              <PlayCircle size={13}/>
            </button>
          ) : (
            <button data-testid="pfd-pause-queue" onClick={pauseQueue}
                    title="Pause generation"
                    style={{ ...iconBtn(), color: COL.amber }}>
              <PauseCircle size={13}/>
            </button>
          )}
          <button data-testid="pfd-cancel-queue" onClick={cancelQueue}
                  title="Cancel generation"
                  style={{ ...iconBtn(), color: COL.red }}>
            <XCircle size={13}/>
          </button>
        </div>
      )}

      {/* ─────────────── DOCUMENTS TAB ─────────────── */}
      {activeTab === "documents" && (
        <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
          {!index ? (
            <EmptyState
              creating={creatingIndex}
              onRunStage={runStage}
              err={err}
            />
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center",
                              justifyContent: "space-between", marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
                    {index.project} — {index.total_sheets} PFD documents
                  </div>
                  <div style={{ fontSize: 11, color: COL.dim, marginTop: 4 }}>
                    Plant type: <span style={{ color: COL.spark }}>{index.plant_type || "generic"}</span>
                    {index.expected_range && (
                      <> · Expected range: {index.expected_range[0]}–{index.expected_range[1]} sheets</>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button data-testid="pfd-generate-all"
                          onClick={generateAll}
                          disabled={isAnyRunning || generatedCount === sheets.length}
                          style={primaryBtn(isAnyRunning || generatedCount === sheets.length)}>
                    <Play size={11}/> Generate All
                  </button>
                  <button data-testid="pfd-export-zip"
                          onClick={exportZip}
                          disabled={generatedCount === 0}
                          style={ghostBtn(generatedCount === 0)}>
                    <Download size={11}/> Export ZIP
                  </button>
                  <button data-testid="pfd-export-line-list"
                          onClick={exportLineList}
                          disabled={generatedCount === 0}
                          style={ghostBtn(generatedCount === 0)}
                          title="Download an Excel line list with every pipe's ISO line number, NPS, service, material, T/P/flow.">
                    <FileSpreadsheet size={11}/> Line List (xlsx)
                  </button>
                  <button data-testid="pfd-recreate-index"
                          onClick={runStage}
                          disabled={creatingIndex}
                          style={ghostBtn(creatingIndex)}>
                    <RefreshCcw size={11}/> Re-create Index
                  </button>
                </div>
              </div>

              {err && (
                <div style={errorBanner()}>
                  <AlertTriangle size={12}/> {err}
                </div>
              )}

              <table data-testid="pfd-doc-list"
                     style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr style={{ background: COL.surface, color: COL.dim }}>
                    <th style={th(34)}>#</th>
                    <th style={th(110)}>Document No.</th>
                    <th style={th()}>Title</th>
                    <th style={th(80)}>Equipment</th>
                    <th style={th(80)}>Status</th>
                    <th style={th(120)}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((s) => {
                    const sid = s.sheet_number === 0
                      ? "BFD-001"
                      : `PFD-${String(s.sheet_number).padStart(3, "0")}`;
                    const row = rowMap[sid] || {};
                    const status = drawings[sid] ? "complete" : (row.status || "not_started");
                    const isExpanded = expandedRow === sid;
                    return (
                      <React.Fragment key={sid}>
                        <tr data-testid={`pfd-row-${sid}`}
                            style={{ borderBottom: `1px solid ${COL.border}`,
                                       background: isExpanded ? COL.surface : "transparent" }}>
                          <td style={td()}>
                            <button onClick={() => setExpandedRow(isExpanded ? null : sid)}
                                    style={{ background: "none", border: 0, color: COL.dim, cursor: "pointer", padding: 0 }}>
                              {isExpanded ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
                            </button>
                          </td>
                          <td style={{ ...td(), color: COL.spark, fontWeight: 700 }}>
                            {sid}
                          </td>
                          <td style={td()}>
                            <div style={{ color: COL.text, fontWeight: 600 }}>{s.title}</div>
                            {s.description && (
                              <div style={{ color: COL.dim, fontSize: 10, marginTop: 3 }}>
                                {s.description}
                              </div>
                            )}
                          </td>
                          <td style={{ ...td(), textAlign: "center", color: COL.dim }}>
                            {s.type === "overview" ? "ALL" : (s.equipment_tags?.length || 0)}
                          </td>
                          <td style={{ ...td(), textAlign: "center" }}>
                            <span data-testid={`pfd-status-${sid}`}>
                              {drawings[sid]?.locked ? "🔒" : STATUS_ICONS[status]} {drawings[sid]?.locked ? "approved" : status}
                            </span>
                          </td>
                          <td style={td()}>
                            {(() => {
                              const isLocked = drawings[sid]?.locked;
                              // ``canvas_svg`` is intentionally excluded from
                              // the LIST endpoint (kept off-payload until the
                              // user actually opens the drawing). So we
                              // gate the View button on STATUS instead — any
                              // ``complete`` drawing has a server-side SVG
                              // ready to fetch on demand. Without this check,
                              // every previously-generated PFD would render
                              // a "Generate" button instead of "View" on a
                              // fresh page load. ``openInViewer`` lazily
                              // fetches the SVG when the user clicks View.
                              const hasSvg = !!drawings[sid]?.canvas_svg
                                              || status === "complete";
                              return (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                                  {hasSvg ? (
                                    <button data-testid={`pfd-view-${sid}`}
                                            onClick={() => openInViewer(sid)}
                                            style={smallBtn()}>
                                      <Eye size={10}/> View
                                    </button>
                                  ) : status === "failed" ? (
                                    <button data-testid={`pfd-retry-${sid}`}
                                            onClick={() => generateOne(s.sheet_number, sid, s.title)}
                                            style={smallBtn(true)}>
                                      <RefreshCcw size={10}/> Retry
                                    </button>
                                  ) : status === "generating" ? (
                                    <span style={{ color: COL.spark, display: "inline-flex", gap: 4, alignItems: "center" }}>
                                      <Loader2 className="pf-spin" size={10}/> Rendering…
                                    </span>
                                  ) : (
                                    <button data-testid={`pfd-gen-${sid}`}
                                            onClick={() => generateOne(s.sheet_number, sid, s.title)}
                                            style={smallBtn()}>
                                      <Play size={10}/> Generate
                                    </button>
                                  )}
                                  {hasSvg && !isLocked && (
                                    <button data-testid={`pfd-launch-editor-${sid}`}
                                            title="Open this drawing in the interactive editor"
                                            onClick={() => {
                                               setActiveSheetId(sid);
                                               setActiveTab("editor");
                                            }}
                                            style={{ ...smallBtn(),
                                               background: "rgba(34,211,238,0.15)",
                                               color: COL.spark,
                                               border: `1px solid ${COL.spark}` }}>
                                      <Pencil size={10}/> Edit
                                    </button>
                                  )}
                                  {hasSvg && !isLocked && (
                                    <>
                                      <button data-testid={`pfd-edit-${sid}`}
                                              title="Edit title / description"
                                              onClick={() => setEditingSheet({
                                                  sheet_number: s.sheet_number,
                                                  title: s.title, description: s.description })}
                                              style={iconBtn()}><Edit3 size={10}/></button>
                                      <button data-testid={`pfd-split-${sid}`}
                                              title="Split into multiple sheets"
                                              disabled={s.equipment_tags.length < 4 || s.type === "overview"}
                                              onClick={() => setSplittingSheet(s)}
                                              style={iconBtn(s.equipment_tags.length < 4 || s.type === "overview")}>
                                        <Split size={10}/></button>
                                      <button data-testid={`pfd-merge-${sid}`}
                                              title="Mark for merge"
                                              disabled={s.type === "overview"}
                                              onClick={() => setMergingSheets((p) =>
                                                p.includes(s.sheet_number)
                                                  ? p.filter(x => x !== s.sheet_number)
                                                  : [...p, s.sheet_number])}
                                              style={iconBtn(s.type === "overview", mergingSheets.includes(s.sheet_number))}>
                                        <GitMerge size={10}/></button>
                                    </>
                                  )}
                                  {hasSvg && (
                                    <>
                                      {isLocked ? (
                                        <button data-testid={`pfd-unlock-${sid}`}
                                                title="Unlock to edit/regenerate"
                                                onClick={() => unlockSheet(s.sheet_number)}
                                                style={iconBtn(false, true)}>
                                          <Lock size={10}/></button>
                                      ) : (
                                        <button data-testid={`pfd-approve-${sid}`}
                                                title="Approve & lock"
                                                onClick={() => approveSheet(s.sheet_number)}
                                                style={iconBtn()}>
                                          <Unlock size={10}/></button>
                                      )}
                                      <button data-testid={`pfd-history-${sid}`}
                                              title="Revision history"
                                              onClick={() => loadRevisions(s.sheet_number, sid)}
                                              style={iconBtn()}>
                                        <History size={10}/></button>
                                      <button data-testid={`pfd-export-${sid}`}
                                              title="Download SVG"
                                              onClick={() => exportSvg(s.sheet_number)}
                                              style={iconBtn()}>
                                        <Download size={10}/></button>
                                      <button data-testid={`pfd-export-dxf-${sid}`}
                                              title="Download DXF (CAD)"
                                              onClick={() => exportDxf(s.sheet_number)}
                                              style={iconBtn()}>
                                        <FileText size={10}/></button>
                                      <button data-testid={`pfd-export-xlsx-${sid}`}
                                              title="Download stream + equipment data (Excel)"
                                              onClick={() => exportXlsx(s.sheet_number)}
                                              style={iconBtn()}>
                                        <FileSpreadsheet size={10}/></button>
                                    </>
                                  )}
                                  {!isLocked && s.type !== "overview" && (
                                    <>
                                      <button data-testid={`pfd-reassign-${sid}`}
                                              title="Move equipment to another sheet"
                                              disabled={(s.equipment_tags || []).length === 0}
                                              onClick={() => setReassigning({
                                                  from_sheet: s.sheet_number,
                                                  available_tags: s.equipment_tags || [],
                                                  selected_tags: [],
                                                  to_sheet: null,
                                              })}
                                              style={iconBtn((s.equipment_tags || []).length === 0)}>
                                        <Move size={10}/></button>
                                      <button data-testid={`pfd-remove-${sid}`}
                                              title="Remove this sheet"
                                              onClick={() => removeSheet(s.sheet_number)}
                                              style={{ ...iconBtn(), color: COL.red,
                                                          borderColor: "rgba(239,68,68,0.4)" }}>
                                        <Trash2 size={10}/></button>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr style={{ background: COL.surface }}>
                            <td/>
                            <td colSpan={5} style={{ padding: "10px 14px 14px",
                                                       borderBottom: `1px solid ${COL.border}` }}>
                              <ExpandedRow sheet={s}/>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* ─────────────── VIEWER TAB ─────────────── */}
      {activeTab === "viewer" && (
        <div data-testid="pfd-viewer-pane"
             style={viewerFullscreen
               ? { position: "fixed", inset: 0, zIndex: 200,
                   background: COL.bg, display: "flex",
                   flexDirection: "column", minHeight: 0 }
               : { flex: 1, display: "flex", flexDirection: "column",
                   minHeight: 0 }}>
          {/* Tab strip — one tab per opened sheet */}
          <div data-testid="pfd-viewer-tabs"
               style={{ display: "flex", overflowX: "auto",
                          borderBottom: `1px solid ${COL.border}`,
                          background: COL.surface }}>
            {openSheetIds.length === 0 ? (
              <div style={{ padding: 14, color: COL.dim, fontSize: 11 }}>
                No PFDs opened yet — go to <b>Documents</b> and click <b>Generate</b> on a row.
              </div>
            ) : openSheetIds.map((sid) => {
              const d = drawings[sid] || {};
              const isActive = sid === activeSheetId;
              return (
                <div key={sid}
                     data-testid={`pfd-viewer-tab-${sid}`}
                     onClick={() => openInViewer(sid)}
                     style={{ padding: "9px 14px", cursor: "pointer",
                                borderRight: `1px solid ${COL.border}`,
                                background: isActive ? COL.bg : "transparent",
                                color: isActive ? COL.spark : COL.dim,
                                borderBottom: isActive ? `2px solid ${COL.spark}` : "2px solid transparent",
                                fontSize: 11, fontWeight: 600,
                                display: "flex", alignItems: "center", gap: 6,
                                whiteSpace: "nowrap" }}>
                  <Layers size={11}/> {sid}
                  <span style={{ color: COL.dim, fontWeight: 400, marginLeft: 4 }}>
                    {(d.title || "").slice(0, 20)}
                  </span>
                  <button onClick={(e) => {
                            e.stopPropagation();
                            setOpenSheetIds((p) => p.filter((x) => x !== sid));
                            if (activeSheetId === sid) {
                              const idx = openSheetIds.indexOf(sid);
                              const next = openSheetIds[idx - 1] || openSheetIds[idx + 1] || null;
                              setActiveSheetId(next);
                            }
                          }}
                          style={{ background: "none", border: 0,
                                     color: COL.dim, cursor: "pointer",
                                     fontSize: 14, padding: "0 0 0 8px" }}>
                    ×
                  </button>
                </div>
              );
            })}
          </div>
          {/* Toolbar — Trimmed: zoom controls moved to the floating
              glass pill at the bottom of the canvas. Keep only the
              export + Edit actions here. */}
          {activeSheetId && drawings[activeSheetId]?.canvas_svg && (
            <div data-testid="pfd-viewer-toolbar"
                 style={{ display: "flex", gap: 6, alignItems: "center",
                            padding: "6px 14px",
                            borderBottom: `1px solid ${COL.border}`,
                            background: COL.surface }}>
              <span data-testid="viewer-zoom-level"
                    style={{ color: COL.dim, fontSize: 11,
                              fontFamily: "'JetBrains Mono', monospace" }}>
                {Math.round(viewerZoom * 100)}%
              </span>
              <span style={{ color: COL.dim, fontSize: 10, marginLeft: 8 }}>
                <Hand size={10} style={{ verticalAlign: "middle" }}/> Drag to pan · Scroll to zoom · 0 to fit · F for fullscreen
              </span>
              <div style={{ flex: 1 }}/>
              {(() => {
                const sheet = (sheets || []).find((s) => {
                  const sid = s.sheet_number === 0 ? "BFD-001"
                                : `PFD-${String(s.sheet_number).padStart(3, "0")}`;
                  return sid === activeSheetId;
                });
                if (!sheet) return null;
                return (
                  <>
                    <button data-testid="viewer-download-pdf"
                            title="Download as PDF"
                            onClick={() => exportPdf(sheet.sheet_number)}
                            style={ghostBtn()}>
                      <Download size={11}/> PDF
                    </button>
                    <button data-testid="viewer-download-dxf"
                            title="Download as DXF (CAD)"
                            onClick={() => exportDxf(sheet.sheet_number)}
                            style={ghostBtn()}>
                      <FileText size={11}/> DXF
                    </button>
                    <button data-testid="viewer-download-svg"
                            title="Download as SVG"
                            onClick={() => exportSvg(sheet.sheet_number)}
                            style={ghostBtn()}>
                      <Download size={11}/> SVG
                    </button>
                    <button data-testid="viewer-open-editor"
                            title="Open this drawing in the interactive editor"
                            onClick={() => {
                              setActiveSheetId(activeSheetId);
                              setActiveTab("editor");
                            }}
                            style={{ ...ghostBtn(),
                                background: "rgba(34,211,238,0.15)",
                                color: COL.spark,
                                borderColor: COL.spark }}>
                      <Pencil size={11}/> Edit
                    </button>
                  </>
                );
              })()}
            </div>
          )}
          {/* Canvas — Autodesk Forge / APS Viewer (SVF2 derivative of the
              SVG). Pan, zoom, fit, fullscreen, measure are now native APS
              tools; the dot-grid background + floating pill toolbar are
              no longer needed. */}
          <div data-testid="pfd-viewer-canvas-wrap"
               style={{ flex: 1, minHeight: 0, position: "relative",
                           display: "flex", flexDirection: "column",
                           background: "#020617" }}>
            {activeSheetId ? (
              <ApsDrawingViewer
                key={activeSheetId}
                projectId={projectId}
                sheetId={activeSheetId}
                docKind="pfd"
                title={drawings[activeSheetId]?.title
                  || (sheets || []).find((s) => s.sheet_id === activeSheetId)?.title
                  || activeSheetId}
                svgHash={_hashSvgFnv(drawings[activeSheetId]?.canvas_svg || "")}
                onExit={() => setActiveTab("editor")}/>
            ) : (
              <div style={{ color: COL.dim, padding: 40, textAlign: "center" }}>
                Select a tab above to view its drawing.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────── EDITOR TAB ─────────────── */}
      {activeTab === "editor" && (
        <div style={{ flex: 1, minHeight: 0, display: "flex",
                            flexDirection: "column" }}
              data-testid="pfd-editor-tab">
          {(() => {
            const sid = activeSheetId
                || (openSheetIds && openSheetIds[0])
                || (sheets && sheets[0]?.sheet_id)
                || (drawings && Object.keys(drawings)[0])
                || null;
            if (!sid) {
              return (
                <div style={{ padding: 24, color: COL.dim, fontSize: 12 }}>
                  No PFD sheets exist yet — create a PFD index first
                  (Documents tab → "Create PFD Index" or run Stage B.2),
                  then return here to edit.
                </div>
              );
            }
            return (
              <PfdEditor key={sid} projectId={projectId} sheetId={sid}
                  sheetTitle={drawings[sid]?.title
                       || (sheets || []).find((s) => s.sheet_id === sid)?.title}
                  onRequestClose={() => setActiveTab("viewer")}
                  onSaved={() => { loadDrawings?.(); }}/>
            );
          })()}
        </div>
      )}

      {/* ─────────────── QUALITY TAB ─────────────── */}
      {activeTab === "quality" && (
        <div style={{ flex: 1, overflow: "auto", padding: 18 }}
             data-testid="pfd-quality-panel">
          <div style={{ marginBottom: 14, display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
              Quality Checks
            </div>
            <button onClick={loadQuality} style={ghostBtn()}>
              <RefreshCcw size={11}/> Refresh
            </button>
          </div>
          {qualityChecks.length === 0 ? (
            <div style={{ color: COL.green, display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle2 size={14}/> All checks passed.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {qualityChecks.map((c, i) => {
                const sev = c.severity;
                const colorMap = { error: "#fca5a5", warning: COL.amber, info: COL.spark };
                return (
                  <div key={i} data-testid={`quality-${c.code}-${i}`}
                       style={{ padding: "8px 12px", background: COL.surface,
                                  borderLeft: `3px solid ${colorMap[sev]}`,
                                  borderRadius: 3, display: "flex", gap: 10,
                                  alignItems: "center", fontSize: 11 }}>
                    <AlertCircle size={12} style={{ color: colorMap[sev] }}/>
                    <span style={{ fontWeight: 700, color: colorMap[sev], textTransform: "uppercase" }}>
                      {sev}
                    </span>
                    <span style={{ color: COL.dim, ...mono }}>{c.code}</span>
                    {c.sheet_number !== undefined && (
                      <span style={{ color: COL.spark, fontWeight: 700 }}>
                        Sheet {c.sheet_number}
                      </span>
                    )}
                    <span style={{ color: COL.text, flex: 1 }}>{c.message}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─────────────── MASTER TABLES TAB ─────────────── */}
      {activeTab === "masters" && (
        <div style={{ flex: 1, overflow: "auto", padding: 18 }}
             data-testid="pfd-masters-panel">
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
              Master Stream Table ({masterStreams.length})
            </div>
            <button data-testid="master-streams-export"
                    onClick={exportMasterStreamsXlsx} style={ghostBtn()}>
              <FileSpreadsheet size={11}/> Export Excel
            </button>
            <button onClick={loadMasters} style={ghostBtn()}>
              <RefreshCcw size={11}/> Refresh
            </button>
          </div>
          <table data-testid="master-streams-table"
                 style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, marginBottom: 28 }}>
            <thead>
              <tr style={{ background: COL.surface, color: COL.dim }}>
                {["Stream", "Name", "From", "To", "Phase", "T (°C)", "P (barg)", "Flow (kg/h)", "Density"].map((h) => (
                  <th key={h} style={th()}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {masterStreams.map((s, i) => {
                const isHi = highlighted.stream === s.stream;
                return (
                  <tr key={i}
                      data-testid={`master-stream-row-${s.stream}`}
                      onClick={() => {
                        setHighlighted((p) => ({
                          ...p, stream: p.stream === s.stream ? null : s.stream,
                        }));
                      }}
                      style={{ borderBottom: `1px solid ${COL.border}`,
                                  cursor: "pointer",
                                  background: isHi ? "rgba(34,211,238,0.16)" : "transparent" }}>
                    <td style={{ ...td(), color: COL.spark, fontWeight: 700 }}>{s.stream}</td>
                    <td style={td()}>{s.name}</td>
                    <td style={td()}>{s.from}</td>
                    <td style={td()}>{s.to}</td>
                    <td style={td()}>{s.phase}</td>
                    <td style={td()}>{s.T_C ?? "—"}</td>
                    <td style={td()}>{s.P_barg ?? "—"}</td>
                    <td style={td()}>{s.flow_kgh ?? "—"}</td>
                    <td style={td()}>{s.density ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
              Master Equipment List ({masterEquipment.length})
            </div>
            <button data-testid="master-equipment-export"
                    onClick={exportMasterEquipmentXlsx} style={ghostBtn()}>
              <FileSpreadsheet size={11}/> Export Excel
            </button>
            <button data-testid="master-index-export"
                    onClick={exportIndexXlsx} style={ghostBtn()}>
              <FileSpreadsheet size={11}/> Index Excel
            </button>
          </div>
          <table data-testid="master-equipment-table"
                 style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: COL.surface, color: COL.dim }}>
                {["Tag", "Name", "Type", "Service", "On Sheets"].map((h) => (
                  <th key={h} style={th()}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {masterEquipment.map((e, i) => {
                const isHi = highlighted.equipment === e.tag;
                return (
                  <tr key={i}
                      data-testid={`master-equipment-row-${e.tag}`}
                      onClick={() => {
                        setHighlighted((p) => ({
                          ...p, equipment: p.equipment === e.tag ? null : e.tag,
                        }));
                      }}
                      style={{ borderBottom: `1px solid ${COL.border}`,
                                  cursor: "pointer",
                                  background: isHi ? "rgba(34,211,238,0.16)" : "transparent" }}>
                    <td style={{ ...td(), color: COL.spark, fontWeight: 700 }}>{e.tag}</td>
                    <td style={td()}>{e.name}</td>
                    <td style={td()}>{e.type}</td>
                    <td style={td()}>{e.service}</td>
                    <td style={td()}>{(e.on_sheets || []).map(n => `PFD-${String(n).padStart(3, "0")}`).join(", ") || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ─────────────── MERGE ACTION BAR ─────────────── */}
      {mergingSheets.length >= 2 && activeTab === "documents" && (
        <div data-testid="merge-action-bar"
             style={{ position: "fixed", bottom: 30, left: "50%",
                        transform: "translateX(-50%)", zIndex: 90,
                        padding: "10px 18px", background: COL.surface2,
                        border: `1px solid ${COL.spark}`, borderRadius: 6,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                        display: "flex", gap: 12, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: COL.text }}>
            <b>{mergingSheets.length}</b> sheets marked for merge:{" "}
            {mergingSheets.map((n) => `#${n}`).join(", ")}
          </span>
          <button data-testid="merge-confirm"
                  onClick={() => mergeSheetsCall(mergingSheets)}
                  style={primaryBtn()}>
            <GitMerge size={11}/> Merge into 1 sheet
          </button>
          <button onClick={() => setMergingSheets([])} style={ghostBtn()}>
            Cancel
          </button>
        </div>
      )}

      {/* ─────────────── EDIT MODAL ─────────────── */}
      {editingSheet && (
        <Modal title={`Edit Sheet PFD-${String(editingSheet.sheet_number).padStart(3, "0")}`}
               onClose={() => setEditingSheet(null)}>
          <label style={modalLabel()}>Title</label>
          <input data-testid="edit-title"
                 value={editingSheet.title || ""}
                 onChange={(e) => setEditingSheet({ ...editingSheet, title: e.target.value })}
                 style={modalInput()}/>
          <label style={modalLabel()}>Description</label>
          <textarea data-testid="edit-description"
                    value={editingSheet.description || ""}
                    onChange={(e) => setEditingSheet({ ...editingSheet, description: e.target.value })}
                    style={{ ...modalInput(), minHeight: 60 }}/>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button data-testid="edit-save"
                    onClick={() => renameSheet(editingSheet.sheet_number, {
                        title: editingSheet.title,
                        description: editingSheet.description,
                    })}
                    style={primaryBtn()}>Save</button>
            <button onClick={() => setEditingSheet(null)} style={ghostBtn()}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ─────────────── SPLIT MODAL ─────────────── */}
      {splittingSheet && (
        <SplitModal sheet={splittingSheet}
                     onCancel={() => setSplittingSheet(null)}
                     onConfirm={(groups) => splitSheet(splittingSheet.sheet_number, groups)}/>
      )}

      {/* ─────────────── RECOVERY DIALOG ─────────────── */}
      {showRecovery && (
        <Modal title="Resume previous PFD generation?"
               onClose={() => setShowRecovery(false)}>
          <div style={{ color: COL.text, fontSize: 12, marginBottom: 14, lineHeight: 1.6 }}>
            A previous Generate-All session was interrupted. Some sheets are
            already complete; <b>{state?.current_job?.sheet_id}</b> ({state?.current_job?.title})
            was being rendered when the page closed. Already-completed PFDs
            are preserved — only the remaining ones will be regenerated.
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button data-testid="recovery-resume"
                    onClick={async () => { await generateAll(); setShowRecovery(false); }}
                    style={primaryBtn()}>
              <Play size={11}/> Resume Generation
            </button>
            <button data-testid="recovery-view-completed"
                    onClick={() => {
                      // Open the first already-complete sheet in the viewer
                      const completed = sheets.find((s) => {
                        const sid = s.sheet_number === 0 ? "BFD-001"
                          : `PFD-${String(s.sheet_number).padStart(3, "0")}`;
                        return drawings[sid]?.canvas_svg;
                      });
                      if (completed) {
                        const sid = completed.sheet_number === 0 ? "BFD-001"
                          : `PFD-${String(completed.sheet_number).padStart(3, "0")}`;
                        openInViewer(sid);
                      }
                      setShowRecovery(false);
                    }}
                    style={ghostBtn()}>
              <Eye size={11}/> View Completed PFDs
            </button>
            <button data-testid="recovery-start-over"
                    onClick={async () => {
                      if (!window.confirm("Start over? This cancels the current queue. Already-completed sheets are kept; you can regenerate them individually.")) return;
                      try { await api.post(`/projects/${projectId}/pfd/cancel`); }
                      catch { /* ignore */ }
                      await loadState();
                      setShowRecovery(false);
                    }}
                    style={ghostBtn()}>
              <RefreshCcw size={11}/> Start Over
            </button>
          </div>
        </Modal>
      )}

      {/* ─────────────── REASSIGN EQUIPMENT MODAL ─────────────── */}
      {reassigning && (
        <ReassignModal
          state={reassigning}
          onChange={setReassigning}
          sheets={sheets}
          onCancel={() => setReassigning(null)}
          onConfirm={(from_sheet, to_sheet, tags) =>
            reassignEquipment(from_sheet, to_sheet, tags)}
        />
      )}
    </div>
  );
}

// ─── Modal helper ───
function Modal({ title, onClose, children }) {
  return (
    <div data-testid="pfd-modal"
         style={{ position: "fixed", inset: 0, zIndex: 100,
                    background: "rgba(0,0,0,0.6)", display: "flex",
                    alignItems: "center", justifyContent: "center" }}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: COL.surface, border: `1px solid ${COL.border}`,
                      borderRadius: 6, padding: 20, minWidth: 480, maxWidth: 720,
                      maxHeight: "80vh", overflow: "auto", color: COL.text,
                      ...mono, fontSize: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: COL.spark }}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}

function SplitModal({ sheet, onCancel, onConfirm }) {
  const tags = sheet.equipment_tags || [];
  const [assignment, setAssignment] = useState(() =>
    Object.fromEntries(tags.map((t, i) => [t, i < tags.length / 2 ? "A" : "B"])));
  const [numGroups, setNumGroups] = useState(2);
  const groups = useMemo(() => {
    const g = {};
    Object.keys(assignment).forEach((t) => {
      const k = assignment[t];
      g[k] = g[k] || [];
      g[k].push(t);
    });
    return Object.keys(g).sort().map((k) => g[k]);
  }, [assignment]);
  return (
    <Modal title={`Split Sheet — ${sheet.title}`} onClose={onCancel}>
      <div style={{ marginBottom: 12, fontSize: 11, color: COL.dim }}>
        Assign each equipment tag to a group (A, B, C…). Each group
        becomes a new sheet.
      </div>
      <label style={modalLabel()}>Number of groups: </label>
      <select data-testid="split-num-groups" value={numGroups}
              onChange={(e) => setNumGroups(Number(e.target.value))}
              style={modalInput()}>
        {[2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <div style={{ marginTop: 14, display: "grid",
                      gridTemplateColumns: "1fr 60px", gap: 6,
                      maxHeight: 320, overflow: "auto" }}>
        {tags.map((t) => (
          <React.Fragment key={t}>
            <div style={{ color: COL.text }}>{t}</div>
            <select data-testid={`split-assign-${t}`}
                    value={assignment[t]}
                    onChange={(e) => setAssignment({ ...assignment, [t]: e.target.value })}
                    style={{ ...modalInput(), padding: "2px 4px" }}>
              {Array.from({ length: numGroups }, (_, i) => String.fromCharCode(65 + i))
                .map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
          </React.Fragment>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: COL.dim }}>
        {groups.map((g, i) => (
          <div key={i}>Group {String.fromCharCode(65 + i)}: {g.length} equipment</div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button data-testid="split-confirm"
                onClick={() => onConfirm(groups)}
                disabled={groups.some(g => g.length < 2)}
                style={primaryBtn(groups.some(g => g.length < 2))}>
          Split into {groups.length} sheets
        </button>
        <button onClick={onCancel} style={ghostBtn()}>Cancel</button>
      </div>
    </Modal>
  );
}

// ───────── helper components ─────────

function ReassignModal({ state, onChange, sheets, onCancel, onConfirm }) {
  const targets = (sheets || []).filter((s) =>
    s.type === "detail" && s.sheet_number !== state.from_sheet);
  const toggleTag = (tag) => onChange({
    ...state,
    selected_tags: state.selected_tags.includes(tag)
      ? state.selected_tags.filter((t) => t !== tag)
      : [...state.selected_tags, tag],
  });
  return (
    <Modal title={`Move equipment from PFD-${String(state.from_sheet).padStart(3, "0")}`}
           onClose={onCancel}>
      <div style={{ marginBottom: 12, fontSize: 11, color: COL.dim }}>
        Pick the equipment to move and the destination sheet. Streams,
        OPCs and process-flow order will be re-resolved automatically.
      </div>
      <label style={modalLabel()}>Equipment to move ({state.selected_tags.length} selected)</label>
      <div style={{ maxHeight: 220, overflow: "auto", padding: 8,
                       background: COL.bg, border: `1px solid ${COL.border}`,
                       borderRadius: 3 }}>
        {state.available_tags.map((tag) => (
          <label key={tag} data-testid={`reassign-tag-${tag}`}
                  style={{ display: "flex", alignItems: "center", gap: 8,
                              padding: "4px 0", cursor: "pointer",
                              fontSize: 12, color: COL.text }}>
            <input type="checkbox"
                   checked={state.selected_tags.includes(tag)}
                   onChange={() => toggleTag(tag)}/>
            {tag}
          </label>
        ))}
      </div>
      <label style={modalLabel()}>Destination sheet</label>
      <select data-testid="reassign-target-sheet"
              value={state.to_sheet || ""}
              onChange={(e) => onChange({ ...state, to_sheet: Number(e.target.value) })}
              style={modalInput()}>
        <option value="">-- pick a sheet --</option>
        {targets.map((s) => (
          <option key={s.sheet_number} value={s.sheet_number}>
            PFD-{String(s.sheet_number).padStart(3, "0")} — {s.title}
            {" "}({(s.equipment_tags || []).length} equipment)
          </option>
        ))}
      </select>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button data-testid="reassign-confirm"
                disabled={!state.to_sheet || state.selected_tags.length === 0}
                onClick={() => onConfirm(state.from_sheet, state.to_sheet,
                                              state.selected_tags)}
                style={primaryBtn(!state.to_sheet || state.selected_tags.length === 0)}>
          <Move size={11}/> Move {state.selected_tags.length} equipment
        </button>
        <button onClick={onCancel} style={ghostBtn()}>Cancel</button>
      </div>
    </Modal>
  );
}

function EmptyState({ creating, onRunStage, err }) {
  return (
    <div style={{ display: "flex", flexDirection: "column",
                    alignItems: "center", padding: "60px 20px",
                    color: COL.dim, fontSize: 12 }}>
      <FileText size={36} style={{ color: COL.dim, marginBottom: 14 }}/>
      <div style={{ fontSize: 13, color: COL.text, fontWeight: 700, marginBottom: 6 }}>
        No PFD documents created yet
      </div>
      <div style={{ marginBottom: 18, maxWidth: 480, textAlign: "center", lineHeight: 1.6 }}>
        Run Stage to analyze your simulation results and create a list of PFD
        documents. You can then generate each PFD drawing individually or
        all at once.
      </div>
      <div style={{ marginBottom: 18, fontSize: 11 }}>
        <div>Prerequisites:</div>
        <div style={{ marginTop: 6, color: COL.green }}>
          ✓ A.1 Design Basis &nbsp;&nbsp; ✓ B.1 Simulation
        </div>
      </div>
      <button data-testid="pfd-run-stage"
              onClick={onRunStage}
              disabled={creating}
              style={{ ...primaryBtn(creating), padding: "9px 22px", fontSize: 12 }}>
        {creating ? (
          <><Loader2 className="pf-spin" size={12}/> Creating index…</>
        ) : (
          <><Play size={12}/> Run Stage — Create PFD Document List</>
        )}
      </button>
      {err && (
        <div style={{ ...errorBanner(), marginTop: 14, maxWidth: 480 }}>
          <AlertTriangle size={12}/> {err}
        </div>
      )}
    </div>
  );
}

function ExpandedRow({ sheet }) {
  return (
    <div style={{ fontSize: 11, color: COL.dim, lineHeight: 1.7 }}>
      {sheet.equipment_tags?.length > 0 && (
        <div>
          <b style={{ color: COL.text }}>Equipment ({sheet.equipment_tags.length}):</b>{" "}
          {sheet.equipment_tags.join(", ")}
        </div>
      )}
      {sheet.equipment_sequence?.length > 0 && sheet.equipment_sequence.join(",") !== sheet.equipment_tags?.join(",") && (
        <div>
          <b style={{ color: COL.text }}>Process flow order:</b>{" "}
          {sheet.equipment_sequence.join(" → ")}
        </div>
      )}
      {sheet.incoming_from_sheets?.length > 0 && (
        <div>
          <b style={{ color: COL.text }}>Incoming OPCs:</b>{" "}
          {sheet.incoming_from_sheets.map((n) => `← Sheet ${n}`).join(", ")}
        </div>
      )}
      {sheet.outgoing_to_sheets?.length > 0 && (
        <div>
          <b style={{ color: COL.text }}>Outgoing OPCs:</b>{" "}
          {sheet.outgoing_to_sheets.map((n) => `→ Sheet ${n}`).join(", ")}
        </div>
      )}
      {sheet.feed_streams?.length > 0 && (
        <div><b style={{ color: COL.text }}>Battery limit feed streams:</b> {sheet.feed_streams.join(", ")}</div>
      )}
      {sheet.product_streams?.length > 0 && (
        <div><b style={{ color: COL.text }}>Battery limit product streams:</b> {sheet.product_streams.join(", ")}</div>
      )}
    </div>
  );
}

// CH.3 — Figma/Miro-style floating pill button for the canvas toolbar.
function pillBtn(disabled, active) {
  return {
    background: active ? "rgba(34,211,238,0.18)" : "transparent",
    border: 0, color: active ? COL.spark : COL.text,
    padding: "6px 10px", borderRadius: 999,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    transition: "background 120ms ease, color 120ms ease",
  };
}


// ─── style helpers ───
function tabStyle(active) {
  return {
    background: active ? COL.surface2 : "transparent",
    color: active ? COL.spark : COL.dim,
    border: 0, padding: "6px 12px", borderRadius: 3,
    cursor: "pointer", fontSize: 11, fontWeight: 600,
    display: "inline-flex", alignItems: "center", gap: 6, ...mono,
  };
}
function primaryBtn(disabled) {
  return {
    background: COL.spark, border: 0, color: "#052e16",
    padding: "6px 14px", borderRadius: 3,
    cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700,
    opacity: disabled ? 0.5 : 1, fontSize: 11,
    display: "inline-flex", alignItems: "center", gap: 6, ...mono,
  };
}
function ghostBtn(disabled) {
  return {
    background: "transparent", border: `1px solid ${COL.border}`,
    color: COL.dim, padding: "6px 14px", borderRadius: 3,
    cursor: disabled ? "not-allowed" : "pointer", fontSize: 11,
    opacity: disabled ? 0.5 : 1,
    display: "inline-flex", alignItems: "center", gap: 6, ...mono,
  };
}
function smallBtn(retry) {
  return {
    background: retry ? "rgba(239,68,68,0.15)" : "rgba(34,211,238,0.15)",
    border: `1px solid ${retry ? COL.red : COL.spark}`,
    color: retry ? "#fca5a5" : COL.spark,
    padding: "3px 8px", borderRadius: 3, cursor: "pointer",
    fontWeight: 600, fontSize: 10,
    display: "inline-flex", alignItems: "center", gap: 4, ...mono,
  };
}
function iconBtn(disabled, active) {
  return {
    background: active ? "rgba(34,211,238,0.25)" : "transparent",
    border: `1px solid ${active ? COL.spark : COL.border}`,
    color: active ? COL.spark : COL.dim,
    padding: "3px 5px", borderRadius: 3,
    cursor: disabled ? "not-allowed" : "pointer", fontSize: 10,
    opacity: disabled ? 0.4 : 1,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 22, height: 22,
  };
}
function modalLabel() {
  return { display: "block", fontSize: 11, color: COL.dim,
            marginBottom: 4, marginTop: 8 };
}
function modalInput() {
  return { width: "100%", padding: "6px 8px", background: COL.bg,
            border: `1px solid ${COL.border}`, borderRadius: 3,
            color: COL.text, fontSize: 12, ...mono };
}
function th(width) {
  return {
    textAlign: "left", padding: "8px 10px",
    borderBottom: `1px solid ${COL.border}`,
    fontWeight: 700, fontSize: 10, textTransform: "uppercase",
    letterSpacing: 0.5, ...(width ? { width } : {}),
  };
}
function td() {
  return { padding: "10px", verticalAlign: "top" };
}
function errorBanner() {
  return {
    padding: "8px 12px", marginBottom: 12,
    background: "rgba(239,68,68,0.10)",
    border: `1px solid ${COL.red}`, borderRadius: 3,
    color: "#fca5a5", display: "flex", gap: 8, alignItems: "center", fontSize: 11,
  };
}
